import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  UsersIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  PhoneIcon,
  EnvelopeIcon,
  TruckIcon,
  ClockIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import apiClient from '../api/config';
import toast from 'react-hot-toast';
import { handleApiError } from '../utils/errorHandler';
import PhoneInput from '../components/common/PhoneInput';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  name?: string; // For backward compatibility
  phone_number: string;
  email?: string;
  language: string;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

interface CustomerStats {
  total_vehicles: number;
  total_services: number;
  total_spent: number;
  last_visit: string | null;
  customer_since: string;
}

const CustomersPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerStats, setCustomerStats] = useState<CustomerStats | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    country_code: '965', // Default to Kuwait
    email: '',
    language: 'ar'
  });

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchTerm]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/customers', {
        params: {
          page: currentPage,
          limit: 20,
          search: searchTerm
        }
      });
      
      // Handle response based on backend format
      if (response.data.data) {
        setCustomers(response.data.data);
      } else if (response.data.customers) {
        setCustomers(response.data.customers);
      } else {
        setCustomers([]);
      }
      
      // Handle pagination
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.total_pages || 1);
      }
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId: number) => {
    try {
      const response = await apiClient.get(`/customers/${customerId}`);
      
      if (response.data.data) {
        setSelectedCustomer(response.data.data);
      } else if (response.data.customer) {
        setSelectedCustomer(response.data.customer);
      }
      
      if (response.data.stats) {
        setCustomerStats(response.data.stats);
      }
      
      setShowDetailsModal(true);
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    }
  };

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/customers', formData);
      toast.success('Customer added successfully');
      setShowAddModal(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    }
  };

  const handleUpdateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    
    try {
      await apiClient.put(`/customers/${selectedCustomer.id}`, formData);
      toast.success('Customer updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      phone_number: '',
      country_code: '965',
      email: '',
      language: 'ar'
    });
    setSelectedCustomer(null);
    setCustomerStats(null);
  };

  const openEditModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setFormData({
      first_name: customer.first_name,
      last_name: customer.last_name,
      phone_number: customer.phone_number,
      country_code: '965', // Default to Kuwait for existing customers
      email: customer.email || '',
      language: customer.language
    });
    setShowEditModal(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className={`container mx-auto px-4 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <UsersIcon className="h-8 w-8 mr-2 text-teal-600" />
            {t('customerManagement')}
          </h1>
          <div className="flex space-x-2">
            <button
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors flex items-center"
            >
              <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
              {t('import')}
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('addCustomer')}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchCustomers')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Customers Table */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-8">
            <UsersIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('noCustomersFound')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customerName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('contact')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customerSince')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('lastVisit')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {customer.first_name} {customer.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {customer.phone_number}
                      </div>
                      {customer.email && (
                        <div className="text-sm text-gray-500 flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {customer.email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(customer.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(customer.last_login)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        customer.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {customer.is_active ? t('active') : t('inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => fetchCustomerDetails(customer.id)}
                        className="text-teal-600 hover:text-teal-900 mr-3"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(customer)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {t('previous')}
              </button>
              <span className="px-4 py-1">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {t('next')}
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Add/Edit Customer Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {showEditModal ? t('editCustomer') : t('addCustomer')}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={showEditModal ? handleUpdateCustomer : handleAddCustomer}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('firstName')}
                  </label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('lastName')}
                  </label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <PhoneInput
                  label={t('phoneNumber')}
                  countryCode={formData.country_code}
                  phoneNumber={formData.phone_number}
                  onCountryCodeChange={(code) => setFormData({ ...formData, country_code: code })}
                  onPhoneNumberChange={(number) => setFormData({ ...formData, phone_number: number })}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('email')} ({t('optional')})
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('preferredLanguage')}
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="ar">{t('arabic')}</option>
                    <option value="en">{t('english')}</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  {showEditModal ? t('updateCustomer') : t('addCustomer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {showDetailsModal && selectedCustomer && customerStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{t('customerDetails')}</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedCustomer(null);
                  setCustomerStats(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Info */}
              <div>
                <h3 className="font-semibold text-lg mb-3">{t('personalInformation')}</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm text-gray-500">{t('name')}</dt>
                    <dd className="font-medium">{selectedCustomer.first_name} {selectedCustomer.last_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">{t('phone')}</dt>
                    <dd className="font-medium">{selectedCustomer.phone_number}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">{t('email')}</dt>
                    <dd className="font-medium">{selectedCustomer.email || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">{t('language')}</dt>
                    <dd className="font-medium">
                      {selectedCustomer.language === 'ar' ? t('arabic') : t('english')}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Customer Stats */}
              <div>
                <h3 className="font-semibold text-lg mb-3">{t('statistics')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center">
                      <TruckIcon className="h-8 w-8 text-teal-600 mr-2" />
                      <div>
                        <p className="text-2xl font-bold">{customerStats.total_vehicles}</p>
                        <p className="text-sm text-gray-500">{t('vehicles')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center">
                      <ClockIcon className="h-8 w-8 text-blue-600 mr-2" />
                      <div>
                        <p className="text-2xl font-bold">{customerStats.total_services}</p>
                        <p className="text-sm text-gray-500">{t('services')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <dl className="space-y-2 mt-4">
                  <div>
                    <dt className="text-sm text-gray-500">{t('customerSince')}</dt>
                    <dd className="font-medium">{formatDate(customerStats.customer_since)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">{t('lastVisit')}</dt>
                    <dd className="font-medium">{formatDate(customerStats.last_visit)}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  openEditModal(selectedCustomer);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('editCustomer')}
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedCustomer(null);
                  setCustomerStats(null);
                }}
                className="px-4 py-2 border rounded-md hover:bg-gray-100"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;